/* eslint-disable react-refresh/only-export-components */
import { useEffect, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { ChevronRight, ChevronDown, Home } from "lucide-react"
import { cn } from "@/lib/utils"
import { AGENT_CONSOLE_GROUPS, AGENT_VIEW_LABEL, agentGroupForView, agentSurfacesForGroup, agentPathForView } from "@/lib/agents-nav"

export interface BreadcrumbSibling {
  label: string
  href: string
}

export interface BreadcrumbItem {
  label: string
  href?: string
  isHome?: boolean
  /** Peer destinations reachable from this level. When present the crumb
   *  grows a chevron that opens a dropdown (on hover or click) so the user
   *  can jump sideways — e.g. Master Lists → Audit, or Companies → People. */
  siblings?: BreadcrumbSibling[]
}

interface BreadcrumbProps {
  items: BreadcrumbItem[]
  className?: string
}

/** One crumb, with an optional sibling dropdown. Hover-intent menu: the whole
 *  crumb (label + chevron) is a single hover target; the menu opens on enter
 *  and stays while the pointer is anywhere within the crumb-or-menu boundary.
 *  The menu's invisible top padding bridges the visual gap so moving from the
 *  crumb onto an item never leaves the hover region; a short close delay
 *  absorbs brief excursions. No click required to open. */
function Crumb({ item, isLast }: { item: BreadcrumbItem; isLast: boolean }) {
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<number | null>(null)
  const hasMenu = !!item.siblings && item.siblings.length > 0

  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null } }
  const scheduleClose = () => { cancelClose(); closeTimer.current = window.setTimeout(() => setOpen(false), 140) }
  useEffect(() => cancelClose, [])

  const label = item.isHome ? <Home className="h-4 w-4" /> : item.label
  const labelCls = isLast ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground transition-colors"

  // Home or no sibling menu → plain link/span.
  if (!hasMenu || item.isHome) {
    if (item.href && !isLast) return <Link to={item.href} className={labelCls}>{label}</Link>
    return <span className={labelCls}>{label}</span>
  }

  // One hover target: label + chevron together. Clicking the label still
  // navigates to the section default; hovering reveals the dropdown.
  const trigger = (
    <span className="inline-flex items-center gap-0.5">
      {label}<ChevronDown className="h-3.5 w-3.5 text-muted-foreground/70" />
    </span>
  )

  return (
    <div
      className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true) }}
      onMouseLeave={scheduleClose}
    >
      {item.href && !isLast ? (
        <Link to={item.href} className={cn("inline-flex items-center", labelCls)}>{trigger}</Link>
      ) : (
        <span className={cn("inline-flex items-center cursor-default", labelCls)}>{trigger}</span>
      )}
      {open && (
        // top-full, no margin; the pt-1 wrapper is a descendant that bridges
        // the gap to the menu so the pointer never leaves the hover region.
        <div className="absolute left-0 top-full z-50 pt-1"
          onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
          <div className="min-w-[180px] rounded-md border bg-background py-1 shadow-lg" role="menu">
            {item.siblings!.map((s) => {
              const active = s.href === item.href
              return (
                <Link
                  key={s.href}
                  to={s.href}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "block px-3 py-1.5 text-sm hover:bg-muted transition-colors",
                    active ? "text-foreground font-medium" : "text-muted-foreground",
                  )}
                >
                  {s.label}
                </Link>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Breadcrumb: Home > Section > Page. Crumbs that declare `siblings` render a
 * dropdown so the user can navigate to peer surfaces at that level.
 */
export function Breadcrumb({ items, className }: BreadcrumbProps) {
  useLocation() // re-render on navigation

  if (items.length === 0) return null

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn("flex items-center gap-1.5 text-sm", className)}
    >
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-1.5">
          {index > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground/50" />}
          <Crumb item={item} isLast={index === items.length - 1} />
        </div>
      ))}
    </nav>
  )
}

const HOME: BreadcrumbItem = { label: "Home", href: "/companies", isHome: true }

/** Admin Console nav model — drives the breadcrumb sibling dropdowns. Keep in
 *  sync with the /admin routes in app/router.tsx. Each section's href points
 *  at its default sub-tab. */
const ADMIN_SECTIONS: { key: string; label: string; href: string }[] = [
  { key: "master-lists", label: "Master Lists", href: "/admin/master-lists/companies" },
  { key: "jobs", label: "Jobs & Operations", href: "/admin/jobs/imports" },
  { key: "audit", label: "Audit", href: "/admin/audit/actions" },
  { key: "user-management", label: "User Management", href: "/admin/user-management/users" },
]

const ADMIN_SUBTABS: Record<string, BreadcrumbSibling[]> = {
  "master-lists": [
    { label: "Companies", href: "/admin/master-lists/companies" },
    { label: "People", href: "/admin/master-lists/people" },
    { label: "Investors", href: "/admin/master-lists/investors" },
  ],
  jobs: [
    { label: "Imports", href: "/admin/jobs/imports" },
    { label: "Exports", href: "/admin/jobs/exports" },
    { label: "Ingestion", href: "/admin/jobs/ingestion" },
  ],
  audit: [
    { label: "Actions", href: "/admin/audit/actions" },
    { label: "Sessions", href: "/admin/audit/sessions" },
  ],
  "user-management": [
    { label: "Users", href: "/admin/user-management/users" },
    { label: "Roles", href: "/admin/user-management/roles" },
    { label: "Permissions", href: "/admin/user-management/permissions" },
  ],
}

/** Module-level store for entity names used in breadcrumbs. */
let _entityLabel: Record<string, string> = {}

/** Set a friendly label for an entity route segment (e.g., company ID → name). */
export function setBreadcrumbLabel(key: string, label: string): void {
  _entityLabel[key] = label
}

/**
 * Get breadcrumb items for a given pathname.
 * Pattern: Home > Entity or Home > Admin Console > Section > Page
 */
export function getBreadcrumbsForPath(pathname: string, search = ""): BreadcrumbItem[] {
  // Companies
  if (pathname === "/companies" || pathname === "/companies/list" || pathname === "/companies/insights") {
    return [HOME, { label: "Companies" }]
  }
  if (pathname.startsWith("/companies/") && !pathname.includes("insights")) {
    const id = pathname.split("/")[2]
    const label = _entityLabel[`company:${id}`] || `Company ${id}`
    return [HOME, { label: "Companies", href: "/companies" }, { label }]
  }

  // People
  if (pathname === "/people" || pathname === "/people/list") {
    return [HOME, { label: "People" }]
  }

  // Investors
  if (pathname === "/investors" || pathname === "/investors/list") {
    return [HOME, { label: "Investors" }]
  }

  // Admin Console — section + sub-tab, each with a sibling dropdown so the
  // user can jump sideways (Master Lists → Audit; Companies → People).
  if (pathname.startsWith("/admin/")) {
    const seg = pathname.split("/")  // ["", "admin", <section>, <subtab>]
    const sectionKey = seg[2]
    const section = ADMIN_SECTIONS.find((s) => s.key === sectionKey)
    if (section) {
      const subtabs = ADMIN_SUBTABS[sectionKey] ?? []
      const subtabKey = seg[3]
      const current = subtabs.find((t) => t.href.endsWith(`/${subtabKey}`))
      return [
        HOME,
        { label: "Admin Console", href: "/admin" },
        // Section crumb: dropdown lists the other admin sections.
        { label: section.label, href: section.href, siblings: ADMIN_SECTIONS.map((s) => ({ label: s.label, href: s.href })) },
        // Sub-tab crumb: dropdown lists the section's peer sub-tabs.
        {
          label: current?.label ?? (subtabKey ? subtabKey.charAt(0).toUpperCase() + subtabKey.slice(1) : section.label),
          href: current?.href,
          siblings: subtabs.length > 1 ? subtabs : undefined,
        },
      ]
    }
  }

  // Admin root
  if (pathname.startsWith("/admin")) {
    return [HOME, { label: "Admin Console" }]
  }

  // UP — Unicorn Prediction. Sub-tabs render as a sibling dropdown so the user
  // can jump sideways (Tabular → Capture) the same way the Admin crumbs work.
  if (pathname === "/up" || pathname.startsWith("/up/")) {
    // Preserve the selected prediction run when jumping between UP tabs.
    const run = new URLSearchParams(search).get("run")
    const runQ = run ? `?run=${run}` : ""
    const UP_SUBTABS: BreadcrumbSibling[] = [
      { label: "Tabular", href: `/up${runQ}` },
      { label: "Analytics", href: `/up/analytics${runQ}` },
      { label: "Capture", href: `/up/performance${runQ}` },
      { label: "Executive Report", href: `/up/executive${runQ}` },
    ]
    const seg = pathname.replace(/\/+$/, "")
    const current = UP_SUBTABS.find((t) => t.href.split("?")[0] === seg) ?? UP_SUBTABS[0]
    return [
      HOME,
      { label: "Unicorn Prediction", href: `/up${runQ}` },
      { label: current.label, href: current.href, siblings: UP_SUBTABS },
    ]
  }

  // Agents (AgentOps console). The console is a single route with its view in
  // ?tab= — render Agents › Group ▾ › Surface ▾ so navigation lives in the
  // breadcrumb exactly like the Admin console's section/sub-tab dropdowns.
  if (pathname === "/agents" || pathname.startsWith("/agents/")) {
    // The active surface is the last path segment (/agents/<group>/<surface>,
    // or /agents/overview); default to overview so breadcrumb, page and
    // sidebar agree.
    const segs = pathname.split("/").filter(Boolean)
    const tail = segs[segs.length - 1]
    const view = tail && tail !== "agents" ? tail : "overview"
    const persona = new URLSearchParams(search).get("persona") || ""
    if (view === "personas") {
      return [HOME, { label: "Agentic Console", href: "/agents" }, { label: "Personas" }]
    }
    // Overview is a single-surface group — show one crumb, not "Overview › Overview".
    if (view === "overview") {
      return [HOME, { label: "Agentic Console", href: "/agents" }, { label: "Overview" }]
    }
    const group = agentGroupForView(view)
    const surfaces = agentSurfacesForGroup(group, persona, view)
    const groupSiblings: BreadcrumbSibling[] = AGENT_CONSOLE_GROUPS.map((g) => ({
      label: g.label, href: agentPathForView(g.views[0], persona),
    }))
    const surfaceSiblings: BreadcrumbSibling[] = surfaces.map((v) => ({
      label: AGENT_VIEW_LABEL[v] ?? v, href: agentPathForView(v, persona),
    }))
    return [
      HOME,
      { label: "Agentic Console", href: "/agents" },
      { label: group.label, href: agentPathForView(group.views[0], persona), siblings: groupSiblings },
      { label: AGENT_VIEW_LABEL[view] ?? view, href: agentPathForView(view, persona), siblings: surfaceSiblings },
    ]
  }

  // Tagging
  if (pathname === "/tags" || pathname.startsWith("/tags/")) {
    return [HOME, { label: "Tagging" }]
  }

  // Settings
  if (pathname === "/settings") {
    return [HOME, { label: "Settings" }]
  }

  // Operator legacy redirects (shouldn't render but just in case)
  if (pathname === "/exports") return [HOME, { label: "Exports" }]
  if (pathname === "/imports") return [HOME, { label: "Imports" }]
  if (pathname === "/ingestion") return [HOME, { label: "Ingestion" }]

  return [HOME]
}
